/** @jsxRuntime classic */
/** @jsx jsx */

import { useEffect } from 'react';
import { labelStyles } from '@zapier/style-encapsulation';
import { SubscribeForm } from 'components/Subscribe/Homepage/Form';
import { mediaQueries } from 'components/mediaQueries';
import { Colors, Typography } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';
import { useSubscribeEmail } from 'hooks/useSubscribeEmail';
import { ScrollTracking, TrackedComponent, useAvroTracking } from 'tracking';

type Props = {
  noPadding?: boolean;
  sectionToTrack?: string;
  loading: boolean;
  error: boolean;
  success: boolean;
  onSubmit: (email: string) => Promise<void>;
};

const Styles = labelStyles('SubscribeMini', {
  outer: css`
    display: grid;
    place-items: center;
  `,
  wrapper: (noPadding?: boolean) => [
    css`
      display: grid;
      place-items: center;
      text-align: center;
      padding: 31px 0 48px 0;
    `,
    css`
      ${mediaQueries.tablet} {
        padding: 22px 0 44px 0;
      }
    `,
    noPadding &&
      css`
        ${mediaQueries.tablet} {
          padding: 0;
        }
      `,
  ],
  title: [
    css`
      font-weight: 800;
      font-size: 19px;
      line-height: 140%;
      padding: 0 20px;
      margin-bottom: 37px;
      color: ${Colors.neutral800};
      ${Typography.paragraph1ShortBold}

      ${mediaQueries.tablet} {
        padding: 0;
        margin-bottom: 28px;
        ${Typography.pageHeader9}
      }

      ${mediaQueries.desktop} {
        margin-bottom: 24px;
      }
    `,
  ],
  paragraph: [
    css`
      color: ${Colors.neutral700};
      font-size: 11px;
      line-height: 130%;
      margin-top: 30px;
      padding: 0 20px;
      ${Typography.smallPrint1Medium}
    `,
  ],
});

export const SubscribeMini = ({
  noPadding,
  loading,
  sectionToTrack = 'mini',
  error,
  success,
  onSubmit,
}: Props) => {
  return (
    <ScrollTracking
      avroIdentifier={TrackedComponent.SubscribeMini}
      css={Styles.outer}
      preAvroIdentifier="Mini Homepage Subscribe CTA"
    >
      <div css={Styles.wrapper(noPadding)} data-testid="subscribe-mini">
        <p css={Styles.title}>
          Get productivity tips delivered straight to your inbox
        </p>
        <SubscribeForm
          loading={loading}
          error={error}
          sectionToTrack={sectionToTrack}
          success={success}
          onSubmit={onSubmit}
        />
        <p css={Styles.paragraph}>
          We’ll email you 1-3 times per week—and never share your information.
        </p>
      </div>
    </ScrollTracking>
  );
};

export const SubscribeMiniContainer = ({
  noPadding,
  sectionToTrack,
}: {
  noPadding?: boolean;
  sectionToTrack?: string;
}) => {
  const { error, loading, success, submitEmail } = useSubscribeEmail();

  const { emitCTAClickEvent } = useAvroTracking();

  useEffect(() => {
    if (success || error) {
      emitCTAClickEvent({
        interaction_raw_text: 'Subscribe',
        custom_tracking_fields: {
          component: TrackedComponent.SubscribeMini,
          success: success || !error,
        },
      });
    }
  }, [success, error, emitCTAClickEvent]);

  return (
    <SubscribeMini
      error={error}
      loading={loading}
      onSubmit={submitEmail}
      noPadding={noPadding}
      sectionToTrack={sectionToTrack}
      success={success}
    />
  );
};
